import _ from 'lodash';
import { useEffect, useMemo, useRef } from 'react';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer } from 'recharts';
import styled from 'styled-components';
import { useItemContext } from '../../contexts/ItemContext';
import { getChartItemColor } from '../../utils/colors';

const barRadius = 5;
const barSize = 20;

// Note: rounding won't be perfect if the items at the ends are too narrow.
const getRadiusArray = (items, i) => {
  return items.length === 1
    ? // Both ends rounded
      [barRadius, barRadius, barRadius, barRadius]
    : i === 0
    ? // Left end rounded
      [barRadius, 0, 0, barRadius]
    : i === items.length - 1
    ? // Right end rounded
      [0, barRadius, barRadius, 0]
    : // No rounding
      [0, 0, 0, 0];
};

const ChartContainer = styled.div`
  height: 50px;
  width: 100%;
  margin-bottom: -20px;
  .recharts-tooltip-wrapper {
    z-index: 100;
  }
`;

const ItemsBarChart = ({ items, accessor }) => {
  const { activeKey, setActiveKey, clearActiveKey } = useItemContext();

  const itemsObject = useMemo(
    // Format the items into an object for recharts.
    () => [_.keyBy(items, 'id')],
    [items],
  );
  const activationTimeout = useRef(null);
  const onMouseEnter = (id) => () => {
    clearTimeout(activationTimeout.current);
    if (activeKey) {
      setActiveKey(id);
    } else {
      // Only delay if there is no active item.
      activationTimeout.current = setTimeout(() => {
        setActiveKey(id);
      }, 100);
    }
  };
  const onMouseLeave = () => {
    clearTimeout(activationTimeout.current);
    clearActiveKey();
  };
  useEffect(() => {
    return () => {
      clearTimeout(activationTimeout.current);
    };
  }, []);

  return (
    <ChartContainer>
      <ResponsiveContainer>
        <BarChart
          data={itemsObject}
          layout="vertical"
          margin={{ top: 0, right: 0, left: 0, bottom: 0 }}
        >
          <XAxis type="number" domain={[0, 'dataMax']} hide />
          <YAxis type="category" hide />
          {items.map(({ id, color, name }, i) => (
            <Bar
              key={id}
              dataKey={(item) => item[id][accessor]}
              barSize={barSize}
              stackId="stack"
              fill={getChartItemColor(id, activeKey, color)}
              radius={getRadiusArray(items, i)}
              onMouseEnter={onMouseEnter(id)}
              onMouseLeave={onMouseLeave}
              name={name}
              animationDuration={0}
              isAnimationActive={false}
              style={activeKey ? { stroke: '#fff', strokeWidth: 1 } : {}}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    </ChartContainer>
  );
};

export default ItemsBarChart;
